/* eslint-disable react/forbid-prop-types */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import DashboardChart from 'components/Chart';
import { useGetProjectProposalsReviewStatus } from 'utils/hooks/dashboard';
import Loading from 'components/Loading';
import { formatShowAppliedFilters, statusColors } from 'utils/func/dashboard';
import DashboardFilter from '../DashboardFilter';

function ProjectProposalPieChart({ organizations, programs }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filters, setFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });

  const [appliedFilters, setAppliedFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });

  const { data, isLoading } = useGetProjectProposalsReviewStatus(appliedFilters, {
    refetchOnWindowFocus: false,
    enabled: isLoadedFilterFromLocalStorage,
  });

  const hasActiveFilters =
    appliedFilters.yearProposed?.length > 0 ||
    appliedFilters.organizationId?.length > 0 ||
    appliedFilters.programId?.length > 0;

  const mapLabels = [
    {
      status: 'submit',
    },
    {
      status: 'prequalification',
    },
    {
      status: 'specialization',
    },
    {
      status: 'finance',
    },
    {
      status: 'approved',
    },
    {
      status: 'notQualified',
    },
  ];

  const chartData = useMemo(
    () => ({
      labels: [
        t('dashboard.projectRevise.label.submit'),
        t('dashboard.projectRevise.label.prequalification'),
        t('dashboard.projectRevise.label.specialization'),
        t('dashboard.projectRevise.label.finance'),
        t('dashboard.projectRevise.label.approved'),
        t('dashboard.projectRevise.label.notQualified'),
      ],
      datasets: [
        {
          data: Object.values(data),
          backgroundColor: Object.values(statusColors.revise),
        },
      ],
    }),
    [data, t]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            generateLabels(chart) {
              const { data: _data } = chart;
              return _data.labels.map((label, i) => {
                const dataset = _data.datasets[0];
                const value = dataset.data[i];
                return {
                  text: `${label} (${value})`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor?.[i] || dataset.backgroundColor[i],
                  lineWidth: 1,
                  index: i,
                  pointStyle: 'circle',
                  hidden: !chart.getDataVisibility(i),
                };
              });
            },
            font: (ctx) => {
              const { width } = ctx.chart;
              return {
                size: Math.max(8, width / 30),
              };
            },
          },
          position: 'bottom',
          onClick: () => {},
        },
        datalabels: {
          formatter: (value, ctx) => {
            let sum = 0;
            const dataArr = ctx.chart.data.datasets[0].data;
            dataArr.forEach((element) => {
              sum += element;
            });
            const percentage = ((value * 100) / sum).toFixed(1);
            if (percentage <= 0) {
              return null;
            }
            return `${percentage}%`;
          },
          color: '#fff',
          font: {
            weight: 'bold',
            size: 12,
          },
          anchor: 'end',
          align: 'start',
          offset: 20,
        },
        tooltip: {
          callbacks: {
            label(ctx) {
              let sum = 0;
              const dataArr = ctx.chart.data.datasets[0].data;
              dataArr.forEach((element) => {
                sum += element;
              });
              const percentage = ((ctx.raw * 100) / sum).toFixed(1);
              if (percentage <= 0) {
                return null;
              }
              return `${t('dashboard.amountOfProjects')}: ${ctx.raw} (${percentage}%)`;
            },
          },
        },
      },
      onHover: (event, activeElements) => {
        const canvas = event.native.target;
        canvas.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const element = elements[0];
          const { index } = element;

          const projectRevisesFilters = {
            programId: appliedFilters?.programId?.length > 0 ? appliedFilters?.programId : [],
            organizationId:
              appliedFilters?.organizationId?.length > 0 ? appliedFilters?.organizationId : [],
            yearProposed:
              appliedFilters?.yearProposed?.length > 0 ? appliedFilters?.yearProposed : [],
          };
          const { status } = mapLabels[index];
          if (status) {
            projectRevisesFilters.statusName = [status];
          }
          localStorage.setItem(
            'admin-projects-revise-filters',
            JSON.stringify(projectRevisesFilters)
          );
          navigate('/projects-revise');
        }
      },
      interaction: {
        intersect: false,
      },
    }),
    [appliedFilters]
  );

  useEffect(() => {
    setIsLoadedFilterFromLocalStorage(true);
    const savedFilters = localStorage.getItem('admin-dashboard-projects-revise-filters');
    if (!savedFilters) {
      return;
    }

    setFilters(JSON.parse(savedFilters));
    setAppliedFilters(JSON.parse(savedFilters));
  }, []);

  if (isLoading) {
    return <Loading />;
  }
  return (
    <div className="card p-3 md:p-5">
      <div className="card-header border-bottom-1 surface-border pb-3 mb-4">
        <div className="flex justify-content-between align-items-center">
          <h3 className="text-2xl text-900 font-semibold m-0">
            {t('dashboard.projectRevise.title')}
          </h3>

          <Button
            icon="pi pi-filter"
            label={t('dashboard.filter.label.button')}
            severity={hasActiveFilters ? 'success' : 'secondary'}
            outlined={!hasActiveFilters}
            onClick={() => setShowFilterDialog(true)}
            className="p-button-sm"
          />
        </div>
      </div>

      <DashboardFilter
        showFilterDialog={showFilterDialog}
        onHide={() => setShowFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        setAppliedFilters={setAppliedFilters}
        organizations={organizations}
        programs={programs}
        isSelectOne={{
          yearProposed: false,
          organizationId: false,
          programId: false,
        }}
        localStorageKey="admin-dashboard-projects-revise-filters"
      />

      <div className="flex flex-column align-items-center md:px-4 md:pb-4 gap-2">
        {formatShowAppliedFilters(appliedFilters, organizations, programs, t)}

        <DashboardChart
          data={chartData}
          options={options}
          type="pie"
          className="w-full"
          style={{ maxWidth: '400px', height: 'auto' }}
        />
      </div>
    </div>
  );
}

ProjectProposalPieChart.propTypes = {
  organizations: PropTypes.array.isRequired,
  programs: PropTypes.array.isRequired,
};

export default ProjectProposalPieChart;
